import React, { useCallback, useEffect, useState } from 'react';
import { adminRequest, AdminApiError } from '../admin-api';
import { reportLocale, type ReportLocale } from '../catalogue-contributions/report-copy';
import { COMMUNITY_REPORT_COPY, fill } from './copy';
import {
  availableDecisions,
  caseDecisionBody,
  readerLabel,
  reportedText,
  type ActiveRestriction,
  type CaseDecision,
  type CommunityCase,
  type CommunityCaseQueue,
  type ModerationAction,
  type RestrictionChoice,
} from './model';

type GetAccessToken = () => Promise<string>;
type Tab = 'queue' | 'log' | 'restrictions';
type Filter = 'all' | 'content' | 'reader';

function formatDate(value: string, locale: ReportLocale) {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(value),
  );
}

/** Admin → Reader reports: one case per reported item or reader, oldest first. */
export function CommunityReports({
  getAccessToken,
  onAccessDenied,
  locale = reportLocale(),
}: {
  getAccessToken: GetAccessToken;
  onAccessDenied(): void;
  locale?: ReportLocale;
}) {
  const copy = COMMUNITY_REPORT_COPY[locale];
  const [tab, setTab] = useState<Tab>('queue');
  const [filter, setFilter] = useState<Filter>('all');
  const [queue, setQueue] = useState<CommunityCaseQueue | null>(null);
  const [log, setLog] = useState<{ items: ModerationAction[]; nextCursor: string | null } | null>(
    null,
  );
  const [restrictions, setRestrictions] = useState<ActiveRestriction[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const request = useCallback(
    (path: string, init?: RequestInit) =>
      adminRequest(getAccessToken, `/admin/community${path}`, init),
    [getAccessToken],
  );
  const failed = useCallback(
    (cause: unknown, message: string) => {
      if (cause instanceof AdminApiError && (cause.status === 401 || cause.status === 403)) {
        onAccessDenied();
        return;
      }
      setError(
        cause instanceof AdminApiError && cause.code === 'COMMUNITY_CASE_ALREADY_RESOLVED'
          ? copy.stale
          : message,
      );
    },
    [copy.stale, onAccessDenied],
  );
  const load = useCallback(
    async (after?: string) => {
      setBusy(true);
      setError(null);
      try {
        if (tab === 'queue') {
          const query = new URLSearchParams();
          if (filter !== 'all') query.set('subject', filter);
          if (after) query.set('after', after);
          const search = query.toString();
          const data = (await request(`/cases${search ? `?${search}` : ''}`)) as CommunityCaseQueue;
          setQueue((old) =>
            after && old ? { ...data, items: [...old.items, ...data.items] } : data,
          );
        } else if (tab === 'log') {
          const data = (await request(
            `/actions${after ? `?after=${encodeURIComponent(after)}` : ''}`,
          )) as { items: ModerationAction[]; nextCursor: string | null };
          setLog((old) =>
            after && old ? { ...data, items: [...old.items, ...data.items] } : data,
          );
        } else {
          setRestrictions(
            ((await request('/restrictions')) as { items: ActiveRestriction[] }).items,
          );
        }
      } catch (cause) {
        failed(cause, copy.failed);
      } finally {
        setBusy(false);
      }
    },
    [copy.failed, failed, filter, request, tab],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const mutate = async (run: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await run();
      setNotice(copy.saved);
    } catch (cause) {
      failed(cause, copy.actionFailed);
    } finally {
      setBusy(false);
    }
    await load();
  };
  const decide = (item: CommunityCase, decision: CaseDecision, note: string) =>
    mutate(() =>
      request('/cases/decision', {
        method: 'POST',
        body: JSON.stringify(caseDecisionBody(item.case, decision, note)),
      }),
    );
  const hidePhoto = (item: CommunityCase) =>
    mutate(() =>
      adminRequest(getAccessToken, `/admin/profile-photos/${item.author!.id}/hide`, {
        method: 'POST',
        body: JSON.stringify({ version: item.author!.avatarVersion }),
      }),
    );
  const restore = (action: ModerationAction) =>
    mutate(() =>
      request('/restorations', {
        method: 'POST',
        body: JSON.stringify({ contentKind: action.content!.kind, contentId: action.content!.id }),
      }),
    );
  const lift = (userId: string) =>
    mutate(() => request(`/restrictions/${encodeURIComponent(userId)}`, { method: 'DELETE' }));

  return (
    <section className="min-w-0 space-y-6" aria-busy={busy} lang={locale}>
      <header className="admin-page-header flex flex-wrap items-start justify-between gap-5">
        <div className="max-w-2xl">
          <h1>{copy.title}</h1>
          <p className="mt-3 text-sm leading-6 text-ink-soft">{copy.intro}</p>
        </div>
        <button
          disabled={busy}
          onClick={() => void load()}
          className="admin-button shrink-0"
          type="button"
        >
          {copy.refresh}
        </button>
      </header>
      <div className="flex flex-wrap gap-2" role="tablist" aria-label={copy.title}>
        {(['queue', 'log', 'restrictions'] as const).map((value) => (
          <button
            key={value}
            role="tab"
            aria-selected={tab === value}
            className={tab === value ? 'admin-button-primary' : 'admin-button'}
            onClick={() => setTab(value)}
            type="button"
          >
            {copy.tabs[value]}
          </button>
        ))}
      </div>
      {error ? (
        <p
          className="rounded-xl border border-danger/25 bg-danger-fill/40 p-4 text-sm leading-6 text-danger"
          role="alert"
        >
          {error}
        </p>
      ) : null}
      {notice ? (
        <p className="text-sm text-ink-soft" role="status">
          {notice}
        </p>
      ) : null}
      {tab === 'queue' ? (
        <>
          <div className="flex flex-wrap items-center gap-2">
            {(['all', 'content', 'reader'] as const).map((value) => (
              <button
                key={value}
                aria-pressed={filter === value}
                className={filter === value ? 'admin-button-primary' : 'admin-button'}
                onClick={() => setFilter(value)}
                type="button"
              >
                {copy.filters[value]}
              </button>
            ))}
            {queue ? (
              <p className="text-sm text-ink-muted">
                {fill(copy.totals, { cases: queue.totalCases, reports: queue.totalPendingReports })}
              </p>
            ) : null}
          </div>
          {busy && !queue ? (
            <p className="admin-empty" role="status">
              {copy.loading}
            </p>
          ) : null}
          {queue && !queue.items.length ? <p className="admin-empty">{copy.empty}</p> : null}
          <div className="space-y-5">
            {queue?.items.map((item) => (
              <CaseCard
                key={item.key}
                item={item}
                locale={locale}
                busy={busy}
                onDecide={(decision, note) => void decide(item, decision, note)}
                onHidePhoto={() => void hidePhoto(item)}
              />
            ))}
          </div>
          {queue?.nextCursor ? (
            <button
              className="admin-button"
              disabled={busy}
              onClick={() => void load(queue.nextCursor!)}
              type="button"
            >
              {copy.loadMore}
            </button>
          ) : null}
        </>
      ) : tab === 'log' ? (
        <ActionLog
          log={log}
          locale={locale}
          busy={busy}
          onRestore={(action) => void restore(action)}
          onMore={(after) => void load(after)}
        />
      ) : (
        <Restrictions
          items={restrictions}
          locale={locale}
          busy={busy}
          onLift={(id) => void lift(id)}
        />
      )}
    </section>
  );
}

export function CaseCard({
  item,
  locale,
  busy,
  onDecide,
  onHidePhoto,
}: {
  item: CommunityCase;
  locale: ReportLocale;
  busy: boolean;
  onDecide(decision: CaseDecision, note: string): void;
  onHidePhoto(): void;
}) {
  const copy = COMMUNITY_REPORT_COPY[locale];
  const [note, setNote] = useState('');
  const [days, setDays] = useState<RestrictionChoice>(7);
  const allowed = availableDecisions(item);
  const kind = item.case.subject === 'reader' ? 'reader' : item.case.contentKind;
  const current = item.content?.current ?? null;
  const author = item.author;
  return (
    <article
      className="admin-panel space-y-5 p-5 sm:p-6"
      data-testid={`community-case-${item.key}`}
    >
      <header className="flex flex-wrap items-baseline justify-between gap-3">
        <h2 className="font-serif text-xl">
          {copy.kinds[kind]}
          {item.content ? ` · ${copy.states[item.content.state]}` : ''}
        </h2>
        <p className="text-sm text-ink-muted">
          {fill(copy.pending, { count: item.pendingCount })}
          {item.previouslyResolvedCount
            ? ` · ${fill(copy.previously, { count: item.previouslyResolvedCount })}`
            : ''}
          {' · '}
          {fill(copy.firstReported, { date: formatDate(item.firstReportedAt, locale) })}
        </p>
      </header>
      {current ? (
        <div className="space-y-2 rounded-lg bg-canvas/55 p-4">
          {current.bookTitle ? (
            <p className="text-xs font-bold uppercase tracking-wider text-ink-muted">
              {copy.book}: {current.bookTitle}
            </p>
          ) : null}
          {item.case.subject === 'content' &&
          item.case.contentKind === 'thought' &&
          current.lineText ? (
            <p className="text-sm italic text-ink-soft">
              {copy.line}: “{current.lineText}”
            </p>
          ) : null}
          {/* Reader text is rendered as text only, never as HTML. */}
          <p className="whitespace-pre-wrap text-base leading-7">{current.body}</p>
          <p className="text-xs text-ink-muted">
            {[
              current.rating !== null ? fill(copy.rating, { value: current.rating }) : null,
              current.containsSpoilers ? copy.spoilers : null,
            ]
              .filter(Boolean)
              .join(' · ')}
          </p>
        </div>
      ) : null}
      <div className="text-sm leading-6">
        <h3 className="font-semibold">{copy.author}</h3>
        {author ? (
          <div className="mt-2 flex items-center gap-3">
            {author.avatarUrl ? (
              <img
                src={author.avatarUrl}
                alt=""
                referrerPolicy="no-referrer"
                className="size-10 rounded-full object-cover"
              />
            ) : null}
            <div>
              <p className="font-semibold">{readerLabel(author)}</p>
              <p className="text-ink-muted">
                {fill(copy.authorHistory, {
                  actioned: author.actionedReportCount,
                  removed: author.removedContentCount,
                })}
              </p>
              {author.restriction ? (
                <p className="text-warning">
                  {author.restriction.endsAt
                    ? fill(copy.restrictedUntil, {
                        date: formatDate(author.restriction.endsAt, locale),
                      })
                    : copy.restrictedPermanent}
                </p>
              ) : null}
            </div>
          </div>
        ) : (
          <p className="text-ink-muted">{copy.anonymous}</p>
        )}
      </div>
      <div className="space-y-3">
        <h3 className="text-sm font-semibold">{copy.reports}</h3>
        {item.reports.map((report) => {
          const seen = reportedText(report, current?.body ?? null);
          return (
            <div key={report.id} className="border-t border-line/65 pt-3 text-sm leading-6">
              <p>
                <strong>
                  {copy.reasons[report.reason as keyof typeof copy.reasons] ?? report.reason}
                </strong>
                {' · '}
                {formatDate(report.createdAt, locale)}
              </p>
              {report.note ? (
                <p className="whitespace-pre-wrap text-ink-soft">{report.note}</p>
              ) : null}
              {seen ? (
                <p className="whitespace-pre-wrap text-ink-muted">
                  {copy.reportedVersion}: {seen}
                </p>
              ) : null}
              <p className="text-xs text-ink-muted">
                {copy.reporter}: {readerLabel(report.reporter) ?? report.reporter.id} ·{' '}
                {fill(copy.reporterHistory, {
                  total: report.reporter.reportCount,
                  recent: report.reporter.reportsLast30Days,
                  dismissed: report.reporter.dismissedCount,
                  actioned: report.reporter.actionedCount,
                })}
              </p>
            </div>
          );
        })}
      </div>
      <fieldset disabled={busy} className="space-y-3">
        <label className="block text-sm">
          {copy.note}
          <textarea
            className="admin-input mt-2 block w-full"
            maxLength={1000}
            value={note}
            onChange={(event) => setNote(event.target.value)}
          />
        </label>
        {allowed.restrict ? (
          <label className="block text-sm">
            {copy.duration}
            <select
              className="admin-input mt-2 block w-full"
              value={days === null ? 'permanent' : String(days)}
              onChange={(event) =>
                setDays(
                  event.target.value === 'permanent'
                    ? null
                    : (Number(event.target.value) as RestrictionChoice),
                )
              }
            >
              {([7, 30, 90] as const).map((value) => (
                <option key={value} value={value}>
                  {copy.durations[value]}
                </option>
              ))}
              <option value="permanent">{copy.durations.permanent}</option>
            </select>
          </label>
        ) : null}
        <div className="flex flex-wrap gap-3">
          <button
            className="admin-button"
            type="button"
            onClick={() => onDecide({ kind: 'dismiss' }, note)}
          >
            {copy.dismiss}
          </button>
          {allowed.remove ? (
            <button
              className="admin-button-danger"
              type="button"
              onClick={() => onDecide({ kind: 'remove' }, note)}
            >
              {copy.remove}
            </button>
          ) : null}
          {allowed.restrict ? (
            <button
              className="admin-button-danger"
              type="button"
              onClick={() => onDecide({ kind: 'restrict', days }, note)}
            >
              {copy.restrict}
            </button>
          ) : null}
          {allowed.removeAndRestrict ? (
            <button
              className="admin-button-danger"
              type="button"
              onClick={() => onDecide({ kind: 'remove_and_restrict', days }, note)}
            >
              {copy.removeAndRestrict}
            </button>
          ) : null}
          {allowed.hidePhoto ? (
            <button className="admin-button-danger" type="button" onClick={onHidePhoto}>
              {copy.hidePhoto}
            </button>
          ) : null}
        </div>
      </fieldset>
    </article>
  );
}

function ActionLog({
  log,
  locale,
  busy,
  onRestore,
  onMore,
}: {
  log: { items: ModerationAction[]; nextCursor: string | null } | null;
  locale: ReportLocale;
  busy: boolean;
  onRestore(action: ModerationAction): void;
  onMore(after: string): void;
}) {
  const copy = COMMUNITY_REPORT_COPY[locale];
  if (!log) return null;
  if (!log.items.length) return <p className="admin-empty">{copy.logEmpty}</p>;
  return (
    <div className="admin-panel divide-y divide-line/65">
      {log.items.map((action) => (
        <div
          key={action.id}
          className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm leading-6"
        >
          <div>
            <p>
              <strong>{copy.actions[action.action]}</strong>
              {action.content ? ` · ${copy.kinds[action.content.kind]}` : ''}
              {action.subject ? ` · ${readerLabel(action.subject)}` : ''}
            </p>
            <p className="text-xs text-ink-muted">
              {formatDate(action.createdAt, locale)}
              {action.moderatorName ? ` · ${fill(copy.by, { name: action.moderatorName })}` : ''}
              {action.action === 'restrict_author'
                ? ` · ${
                    typeof action.details.endsAt === 'string'
                      ? fill(copy.until, { date: formatDate(action.details.endsAt, locale) })
                      : copy.permanent
                  }`
                : ''}
            </p>
            {action.note ? (
              <p className="whitespace-pre-wrap text-ink-soft">{action.note}</p>
            ) : null}
          </div>
          {action.action === 'remove_content' && action.removalOpen ? (
            <button
              className="admin-button"
              disabled={busy}
              type="button"
              onClick={() => onRestore(action)}
            >
              {copy.restore}
            </button>
          ) : null}
        </div>
      ))}
      {log.nextCursor ? (
        <div className="p-4">
          <button
            className="admin-button"
            disabled={busy}
            type="button"
            onClick={() => onMore(log.nextCursor!)}
          >
            {copy.loadMore}
          </button>
        </div>
      ) : null}
    </div>
  );
}

function Restrictions({
  items,
  locale,
  busy,
  onLift,
}: {
  items: ActiveRestriction[] | null;
  locale: ReportLocale;
  busy: boolean;
  onLift(userId: string): void;
}) {
  const copy = COMMUNITY_REPORT_COPY[locale];
  if (!items) return null;
  if (!items.length) return <p className="admin-empty">{copy.restrictionsEmpty}</p>;
  return (
    <div className="admin-panel divide-y divide-line/65">
      {items.map((item) => (
        <div
          key={item.userId}
          className="flex flex-wrap items-center justify-between gap-3 p-4 text-sm leading-6"
        >
          <div>
            <p className="font-semibold">{readerLabel(item) ?? item.userId}</p>
            <p className="text-xs text-ink-muted">
              {fill(copy.since, { date: formatDate(item.startsAt, locale) })} ·{' '}
              {item.endsAt
                ? fill(copy.until, { date: formatDate(item.endsAt, locale) })
                : copy.permanent}
              {item.moderatorName ? ` · ${fill(copy.by, { name: item.moderatorName })}` : ''}
            </p>
          </div>
          <button
            className="admin-button"
            disabled={busy}
            type="button"
            onClick={() => onLift(item.userId)}
          >
            {copy.lift}
          </button>
        </div>
      ))}
    </div>
  );
}
